import React, { FormEvent, FormEventHandler, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import { useT, UT } from '@transifex/react';
import { PasswordField, renderHTMLTags, TextField, Typography } from '@raisin/components-lib';
import ChevronRight from '@raisin/components-lib/Icons/ChevronRight';
import {
  getForgotPasswordLink,
  getVerificationMessage,
  OTP as Otp,
  RAISIN_LOGIN_CLIENT,
  useFingerprinting,
} from '@raisin/cp-auth';
import { isExpiredSolutionToken } from '@raisin/cp-captcha';
import {
  dateFormatter,
  getRelativePathWithLocalePrefix,
  isCOM,
  isPolishDomain,
  LINKS,
  TechnicalErrorCode,
  useFeatureFlag,
} from '@raisin/cp-utils';
import { isExponeaInitialized, trackEvent } from '@raisin/events-tracking';
import { CircleProgress } from 'components/CircleProgress';
import { RememberDeviceConsentComponent } from 'components/RememberDeviceConsent';
import {
  CREATE_ACCOUNT_CLICKED,
  FORGOT_PASSWORD_CLICKED,
  LOGIN_FAILED,
  LOGIN_LOADED,
} from 'eventsTracking';
import { useLoginCaptcha } from 'hooks/useLoginCaptcha';
import { useLoginForm } from 'hooks/useLoginForm';
import {
  DEEP_LINK_STORAGE_KEY,
  ErrorStatus,
  ErrorStatusType,
  JEOPARDY_DEEP_LINKS,
  LOGIN_MTAN_EVENT_TRACKING_OBJECT,
} from '../../constants';
import * as S from './styles';

interface MainComponentPops {
  locale: string;
}

export enum TestId {
  EMAIL_INPUT = 'username',
  PASSWORD_INPUT = 'password',
  LOGIN_BUTTON = 'loginButton',
  ERROR_MSG = 'errorMessage',
}

// workaround from https://github.com/mui/material-ui/issues/14427#issuecomment-1703996786
const usePrefillFix = () => {
  const [isAutoFill, setIsAutoFill] = useState(false);

  return {
    InputLabelProps: { shrink: isAutoFill || undefined },
    InputProps: {
      onAnimationStart: (event: React.AnimationEvent<HTMLDivElement>) => {
        event.animationName === 'mui-auto-fill' && setIsAutoFill(true);
      },
      onAnimationEnd: (e: React.AnimationEvent<HTMLDivElement>) =>
        e.animationName === 'mui-auto-fill-cancel' && setIsAutoFill(false),
      onFocus: () => setIsAutoFill(false),
    },
  };
};

export const LoginComponent: React.FC<MainComponentPops> = ({ locale }) => {
  const t = useT();
  const { formatDate, formatTime } = useIntl();
  const [forgotPasswordLink, setForgotPasswordLink] = useState('');
  const { variation, isLoading: featureFlagsLoading } = useFeatureFlag();

  const umtLoginErrorMessageForBlockedUsers =
    variation?.('umtLoginErrorMessageForBlockedUsers', false) ?? false;

  const umtFESolveFriendlyCaptchaPuzzleDuringLogin =
    !featureFlagsLoading &&
    (variation?.('umtFESolveFriendlyCaptchaPuzzleDuringLogin', true) ?? true);
  const umtShowSystemErrorInLoginPage =
    variation?.('umtShowSystemErrorInLoginPage', false) ?? false;
  const umtVerifyDeviceFingerprintingDuringLoginSardine =
    variation?.('umtVerifyDeviceFingerprintingDuringLoginSardine', false) ?? false;

  const { sessionKey } = useFingerprinting({
    featureFlag: umtVerifyDeviceFingerprintingDuringLoginSardine,
  });

  const handleSubmissionErrorRef =
    useRef<ReturnType<typeof useLoginForm>['handleSubmissionError']>();
  const {
    formDisabledDueToCaptcha,
    getCaptchaSolutionToken,
    isSubmittingAfterOtp,
    isSubmittingInitialCaptcha,
    resetCaptcha,
    submitInitialLoginWithCaptcha,
    submitLoginAfterOtp,
    widget: captchaWidget,
  } = useLoginCaptcha({
    handleOtpCaptchaFailure: () => {
      handleSubmissionErrorRef.current?.({ code: TechnicalErrorCode });
    },
  });

  const {
    fieldErrors,
    handleBlur,
    loginFormError,
    touched,
    values,
    handleChange,
    isSubmitting,
    redirectionInProgress,
    submitForm,
    handleSubmissionError,
    showOTP,
    verificationId,
    customerId,
    guestToken,
    userId,
    showRememberDeviceConsent,
    setRememberDeviceConsentGiven,
  } = useLoginForm({
    locale: locale,
    clientId: RAISIN_LOGIN_CLIENT,
    umtLoginErrorMessageForBlockedUsers,
    umtVerifyDeviceFingerprintingDuringLoginSardine,
    getFingerprintingSessionKey: () => sessionKey,
    umtShowSystemErrorInLoginPage,
    resetFriendlyCaptcha: resetCaptcha,
    getCaptchaSolutionToken,
  });

  handleSubmissionErrorRef.current = handleSubmissionError;

  const onUserVerified = async () => {
    submitLoginAfterOtp(submitForm);
  };

  const emailPrefillFix = usePrefillFix();
  const passwordPrefillFix = usePrefillFix();

  const onUserVerificationFailed = async () => {
    trackEvent({ ...LOGIN_FAILED, customAttribute1: customerId });
  };

  const cleanUpErrorDeepLink = (storage: Storage) => {
    const deeplink = storage.getItem(DEEP_LINK_STORAGE_KEY);
    if (deeplink && JEOPARDY_DEEP_LINKS.includes(deeplink)) {
      storage.removeItem(DEEP_LINK_STORAGE_KEY);
    }
  };

  useEffect(() => {
    // workaround to clean up deeplink with specific value that causes error from both localStorage and sessionStorage
    cleanUpErrorDeepLink(localStorage);
    cleanUpErrorDeepLink(sessionStorage);

    setForgotPasswordLink(getForgotPasswordLink({ locale }));
  }, [locale]);

  useEffect(() => {
    // track to snowplow only
    trackEvent(LOGIN_LOADED, false, true);
  }, []);

  const exponeaAvailable = isExponeaInitialized();
  useEffect(() => {
    if (exponeaAvailable) {
      // track to exponea only
      trackEvent(LOGIN_LOADED, true, false);
    }
  }, [exponeaAvailable]);

  const renderOTP = () => {
    const renderDescription = (mobile: string, date: Date) => (
      <Typography variant="body1">
        <UT
          _str="otp.login.description"
          mobile={mobile}
          date={dateFormatter(date, formatDate)}
          time={formatTime(date)}
          {...renderHTMLTags}
        />
      </Typography>
    );

    return (
      <S.OTP>
        <Otp
          verificationMessage={getVerificationMessage()}
          verificationId={verificationId}
          customer={{
            customerId,
            guestToken,
            userId,
          }}
          eventObject={LOGIN_MTAN_EVENT_TRACKING_OBJECT}
          onUserVerified={onUserVerified}
          onUserVerificationFailed={onUserVerificationFailed}
          locale={locale}
          description={renderDescription}
          underInputComponent={
            showRememberDeviceConsent && (
              <RememberDeviceConsentComponent
                onConsentChange={(newValue: boolean) => setRememberDeviceConsentGiven(newValue)}
              />
            )
          }
        />
      </S.OTP>
    );
  };

  const errorMessage = (errorStatus: ErrorStatusType) => {
    const messageMapping: Record<ErrorStatusType, string> = {
      [ErrorStatus.MtanDisabled]: 'login.error.mtanDisabled.description',
      [ErrorStatus.InvalidGrant]: 'login.error.invalidGrant.description',
      [ErrorStatus.AccountLocked]: 'login.error.accountLocked.description',
      [ErrorStatus.GeneralError]: 'login.error.general.description',
      [ErrorStatus.SystemError]: 'login.error.systemError.description',
    };

    return messageMapping[errorStatus] ?? 'login.error.general.description';
  };

  const renderErrorMessage = () => {
    if (loginFormError) {
      return (
        <S.ErrorAlert mode="error" testId="TestId.ERROR_MSG">
          <Typography>{t(errorMessage(loginFormError))}</Typography>
        </S.ErrorAlert>
      );
    }
    return null;
  };

  const onForgotPasswordClicked = () => {
    trackEvent(FORGOT_PASSWORD_CLICKED, true);
  };

  const onCreateAccountClicked = () => {
    trackEvent(CREATE_ACCOUNT_CLICKED, true);
  };

  const formSubmitHandler: FormEventHandler = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (umtFESolveFriendlyCaptchaPuzzleDuringLogin) {
      const solution = getCaptchaSolutionToken();

      const isExpired = isExpiredSolutionToken(solution!);
      if (isExpired) {
        await resetCaptcha();
      }
    }

    submitInitialLoginWithCaptcha(submitForm);
  };

  function renderLoginForm() {
    return (
      <>
        <S.LoginForm>
          <S.LoginFormTitle>
            <Typography variant="h5">{t('login.form.title')}</Typography>
          </S.LoginFormTitle>
          <form onSubmit={formSubmitHandler} noValidate>
            <Box display="flex" flexDirection="column" margin="auto">
              {renderErrorMessage()}
              <S.LoginFormField>
                <TextField
                  error={touched.email && Boolean(fieldErrors.email)}
                  id="email"
                  type="email"
                  autoFocus
                  inputProps={{
                    'data-testid': TestId.EMAIL_INPUT,
                    autocomplete: 'username',
                  }}
                  label={t('login.form.email.label')}
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={
                    touched.email && Boolean(fieldErrors.email)
                      ? t('login.form.username.validation.error.message')
                      : ' ' // whitespace string to reserve space for the error message, see UM-5295
                  }
                  {...emailPrefillFix}
                />
              </S.LoginFormField>
              <S.LoginFormField>
                <PasswordField
                  id="password"
                  inputProps={{
                    'data-testid': TestId.PASSWORD_INPUT,
                    autocomplete: 'current-password',
                  }}
                  passwordVisibilityToggle
                  error={touched.password && Boolean(fieldErrors.password)}
                  label={t('login.form.password.label')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.password}
                  {...passwordPrefillFix}
                  InputProps={{
                    ...passwordPrefillFix.InputProps,
                  }}
                />
                <S.ForgotPasswordButton
                  mode="primaryText"
                  endIcon={<ChevronRight />}
                  href={forgotPasswordLink}
                  onClick={onForgotPasswordClicked}
                >
                  {t('login.forgotpassword.link')}
                </S.ForgotPasswordButton>
              </S.LoginFormField>
              {captchaWidget}
              <S.LoginSubmitButton
                type="submit"
                disabled={formDisabledDueToCaptcha}
                mode="primary"
                data-testid={TestId.LOGIN_BUTTON}
                loading={isSubmitting || redirectionInProgress || isSubmittingInitialCaptcha}
              >
                {t('login.form.submit.label')}
              </S.LoginSubmitButton>
              {(!isCOM() || isPolishDomain()) && (
                <S.RegistrationButton
                  mode="secondary"
                  href={`${getRelativePathWithLocalePrefix({
                    path: LINKS.REGISTRATION,
                    fallbackLocale: locale,
                  })}`}
                  onClick={onCreateAccountClicked}
                >
                  {t('login.registration.link')}
                </S.RegistrationButton>
              )}
            </Box>
          </form>
        </S.LoginForm>
      </>
    );
  }

  const renderContent = () => {
    if (isSubmittingAfterOtp) {
      return (
        <>
          <CircleProgress />
          {captchaWidget}
        </>
      );
    }

    if (showOTP) return renderOTP();
    return renderLoginForm();
  };

  return <S.Login>{renderContent()}</S.Login>;
};
